import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faEdit, faSave, faSpinner, faUser, faBarcode,
  faTags, faInfoCircle, faShoppingCart, faTruck,
  faEuroSign, faAppleAlt, faRulerCombined, faFish, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../ui/ToastProvider';
import { getArticleById, updateArticle } from '../../services/articleApi';
import { articleWorkflowApi } from '../../services/articleWorkflowApi';
import { SupplierArticle, ArticleStatus } from '../../types/article';
import { PACKAGING_UNITS, CONTENT_UNITS } from '../../constants/articleOptions';
import ArticleDetailVertrieb from './ArticleDetailVertrieb';
import ArticleDetailMarketing from './ArticleDetailMarketing';

import ArticleDetailNaehrwerte from './ArticleDetailNaehrwerte';
import ArticleDetailEinheiten from './ArticleDetailEinheiten';
import { ProductDataTab } from './ArticleDetailEinkauf';
import { FischTab } from './ArticleDetailFisch';
import { useGlobalData } from '../../contexts/GlobalDataContext';
import { ChangeTrackingWrapper, ChangeIndicator } from '../common/ChangeIndicator';
import { ArticleStatusBadge } from '../common/ArticleStatusBadge';
import changeTrackingService from '../../services/changeTrackingService';

const logger = {
  error: (message: string, ...args: any[]) => console.error(message, ...args)
};

interface ArticleDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  articleId: string | null;
}

type TabType = 'einkauf' | 'naehrwerte' | 'fisch' | 'einheiten' | 'vertrieb' | 'marketing';

// **UTILITY FUNCTION FOR CHANGE DETECTION (updated)**
const getFieldValue = (obj: any, path: string) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

const isFieldChanged = (fieldPath: string, originalData: any, currentData: any): boolean => {
  if (!originalData || !currentData) return false;
  
  const originalValue = getFieldValue(originalData, fieldPath);
  const currentValue = getFieldValue(currentData, fieldPath);
  
  // Handle null/undefined cases
  if (!originalValue && !currentValue) return false;
  if (!originalValue && currentValue) return true;
  if (originalValue && !currentValue) return true;
  
  // For arrays
  if (Array.isArray(originalValue) && Array.isArray(currentValue)) {
    if (originalValue.length !== currentValue.length) return true;
    return originalValue.some((item, index) => 
      JSON.stringify(item) !== JSON.stringify(currentValue[index])
    );
  }
  
  // For objects
  if (typeof originalValue === 'object' && typeof currentValue === 'object') {
    return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
  }
  
  // For primitive values
  return originalValue !== currentValue;
};

const isFieldNew = (fieldPath: string, originalData: any, currentData: any): boolean => {
  if (!originalData || !currentData) return false;
  
  const originalValue = getFieldValue(originalData, fieldPath);
  const currentValue = getFieldValue(currentData, fieldPath);
  
  // Field is new if original was empty/null and current has a value
  const originalEmpty = !originalValue || originalValue === '' || originalValue === null;
  const currentHasValue = currentValue && currentValue !== '' && currentValue !== null;
  
  return originalEmpty && currentHasValue;
};

// **ENHANCED FIELD CHANGE INDICATOR WITH CONTEXT MENU**
interface FieldHistoryProps {
  fieldName: string;
  fieldPath: string;
  originalData: any;
  currentData: any;
  children: React.ReactNode;
  articleNumber?: string;
}

const FieldWithHistory: React.FC<FieldHistoryProps> = ({ 
  fieldName, 
  fieldPath, 
  originalData, 
  currentData, 
  children,
  articleNumber
}) => {
  const isChanged = isFieldChanged(fieldPath, originalData, currentData);
  const isNew = isFieldNew(fieldPath, originalData, currentData);

  return (
    <ChangeIndicator
      fieldName={fieldName}
      fieldPath={fieldPath}
      hasChanges={isChanged}
      isNew={isNew}
      articleNumber={articleNumber}
    >
      {children}
    </ChangeIndicator>
  );
};

const ArticleDetailModal: React.FC<ArticleDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  articleId 
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('einkauf');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [data, setData] = useState<SupplierArticle | null>(null);
  const [currentData, setCurrentData] = useState<SupplierArticle | null>(null);
  const [originalData, setOriginalData] = useState<SupplierArticle | null>(null);
  const [initialEditData, setInitialEditData] = useState<SupplierArticle | null>(null); // NEW: Bewahrt Daten beim Bearbeiten
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acceptingChanges, setAcceptingChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { showSuccess, showError } = useToast();
  
  // Use global data context instead of loading individually
  const { 
    ecrCodes, 
    brands, 
    labels, 
    dropdownData, 
    isLoading: globalDataLoading,
    isReady: globalDataReady 
  } = useGlobalData();

  useEffect(() => {
    if (isOpen && articleId && globalDataReady) {
      loadArticleData();
    }
  }, [isOpen, articleId, globalDataReady]);

  const loadArticleData = async () => {
    if (!articleId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('[ArticleDetailModal] Loading article data...');
      const articleData = await getArticleById(articleId);

      console.log('[MODAL] Article data loaded:', articleData?.articleNumber);
      console.log('[MODAL] Images found:', {
        mainImage: articleData?.images?.mainImage ? 'Present' : 'Missing',
        backImages: articleData?.images?.backImages?.length || 0,
        labelImages: articleData?.images?.labelImages?.length || 0
      });

      // For change tracking: use submissionSnapshot as original data
      const originalDataForTracking = articleData?.submissionSnapshot || articleData;

      console.log('[MODAL] Change tracking data:', {
        hasSubmissionSnapshot: !!articleData?.submissionSnapshot,
        articleStatus: articleData?.status,
        changedFields: articleData?.changedFields,
        snapshotDescription: articleData?.submissionSnapshot?.description,
        currentDescription: articleData?.description,
        willShowChanges: !!articleData?.submissionSnapshot && articleData?.submissionSnapshot !== articleData
      });

      setData(articleData);
      setOriginalData(originalDataForTracking);
      setCurrentData(articleData);
      
    } catch (err) {
      console.error('Error loading article:', err);
      setError('Fehler beim Laden des Artikels');
      showError('Fehler beim Laden des Artikels');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (!currentData) return;

    setCurrentData(prev => ({
      ...prev!,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!currentData || !articleId) return;

    setSaving(true);
    try {
      const updatedArticle = await updateArticle(articleId, currentData);
      setData(updatedArticle);
      setOriginalData(updatedArticle);
      setMode('view');
      setInitialEditData(null);
      showSuccess('Artikel erfolgreich gespeichert');
    } catch (err) {
      console.error('Error saving article:', err);
      showError('Fehler beim Speichern des Artikels');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (initialEditData) {
      setCurrentData(initialEditData);
    }
    setMode('view');
    setInitialEditData(null);
  };

  const handleAcceptChanges = async () => {
    if (!currentData?.articleNumber || !currentData._id) return;

    setAcceptingChanges(true);
    try {
      // TODO: Get actual admin user ID from auth context
      const adminUserId = 'admin'; // Placeholder - should be from auth context
      
      // 1. Accept the changes (remove change indicators)
      await changeTrackingService.acceptChanges(currentData.articleNumber, adminUserId);
      
      // 2. Update article status to approved
      const result = await articleWorkflowApi.updateArticleStatus(
        currentData._id,
        'approved',
        'Änderungen akzeptiert und Artikel genehmigt'
      );

      if (result.success) {
        // 3. Reload article data to reflect new status and remove change indicators
        await loadArticleData();
        
        showSuccess('Änderungen wurden akzeptiert und Artikel wurde genehmigt');
      }
    } catch (err) {
      logger.error('Error accepting changes:', err);
      showError('Fehler beim Akzeptieren der Änderungen');
    } finally {
      setAcceptingChanges(false);
    }
  };

  const handleClose = () => {
    setMode('view');
    setActiveTab('einkauf');
    setData(null);
    setCurrentData(null);
    setOriginalData(null);
    setInitialEditData(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black bg-opacity-70 flex items-start justify-center p-2 sm:p-4 overflow-y-auto"
        style={{ zIndex: 999999 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-none flex flex-col my-4 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <FontAwesomeIcon icon={faBarcode} className="text-blue-600 text-xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {/* Marke + Bezeichnung1 + Bezeichnung2 + Inhalt + InhaltEinheit */}
                  {(() => {
                    const parts = [];
                    if (data?.brand) parts.push(data.brand);
                    if (data?.description) parts.push(data.description);
                    if (data?.description2) parts.push(data.description2);
                    if (data?.content && data?.contentUnit) {
                      parts.push(`${data.content} ${data.contentUnit}`);
                    } else if (data?.content) {
                      parts.push(data.content.toString());
                    }
                    return parts.length > 0 ? parts.join(' ') : 'Artikel Details';
                  })()}
                </h2>
                <p className="text-gray-600">
                  {data?.gtin && `GTIN: ${data.gtin}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {mode === 'edit' ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    {saving ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faSave} />
                    )}
                    Speichern
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Abbrechen
                  </button>
                </>
              ) : (
                <>
                  {/* Accept Changes Button - nur anzeigen wenn es Änderungen gibt */}
                  {currentData?.changedFields && currentData.changedFields.length > 0 && (
                    <button
                      onClick={handleAcceptChanges}
                      disabled={acceptingChanges}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      {acceptingChanges ? (
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faCheckCircle} />
                      )}
                      Änderungen akzeptieren
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setInitialEditData(currentData); // Speichere aktuelle Daten vor Bearbeitung
                      setMode('edit');
                    }}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    Bearbeiten
                  </button>
                </>
              )}
              
              <button
                onClick={handleClose}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          {!loading && currentData ? (
            <ChangeTrackingWrapper 
              articleNumber={currentData.articleNumber}
              onChangesViewed={() => {
                // Reload article data to refresh status
                loadArticleData();
              }}
            >
              {(changeData) => (
                <>
                  {loading || globalDataLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-6xl text-blue-500 mb-4 animate-spin" />
                <p className="text-xl text-gray-600">
                  {loading ? 'Artikel wird geladen...' : 'Daten werden vorgeladen...'}
                </p>
                {globalDataLoading && (
                  <p className="text-sm text-gray-500 mt-2">
                    Dropdown-Optionen, ECR-Codes, Marken und Labels werden geladen...
                  </p>
                )}
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-xl text-red-600">Artikel konnte nicht geladen werden</p>
              </div>
            </div>
          ) : (
            <div className="flex h-[calc(95vh-120px)]">
              {/* Sidebar Navigation */}
              <div className="w-80 bg-gradient-to-b from-gray-50 to-gray-100 border-r border-gray-200 p-6">
                <nav className="space-y-3">
                  <button
                    onClick={() => setActiveTab('einkauf')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'einkauf' 
                        ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-3 text-lg" />
                    <span className="font-medium">Einkauf</span>
                  </button>
                  
                   <button
                    onClick={() => setActiveTab('einheiten')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'einheiten' 
                        ? 'bg-teal-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faRulerCombined} className="mr-3 text-lg" />
                    <span className="font-medium">Einheiten & Barcodes</span>
                  </button>
                  
                   <button
                    onClick={() => setActiveTab('vertrieb')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'vertrieb' 
                        ? 'bg-purple-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faTruck} className="mr-3 text-lg" />
                    <span className="font-medium">Vertrieb</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('marketing')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'marketing' 
                        ? 'bg-orange-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faTags} className="mr-3 text-lg" />
                    <span className="font-medium">Marketing</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('naehrwerte')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'naehrwerte' 
                        ? 'bg-red-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faAppleAlt} className="mr-3 text-lg" />
                    <span className="font-medium">Nährwerte</span>
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('fisch')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'fisch' 
                        ? 'bg-cyan-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faFish} className="mr-3 text-lg" />
                    <span className="font-medium">Fisch</span>
                  </button>
                </nav>

              </div>

              {/* Main Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <div className="p-8">
                  {activeTab === 'einkauf' && (
                    <ProductDataTab 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      preloadedEcrCodes={ecrCodes}
                      preloadedBrands={brands}
                      preloadedLabels={labels}
                      originalData={originalData}
                      currentData={currentData}
                      articleNumber={currentData?.articleNumber}
                      isSupplierMode={false}
                    />
                  )}
          
                  {activeTab === 'naehrwerte' && (
                    <ArticleDetailNaehrwerte 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                      isSupplierMode={false}
                    />
                  )}
                  
                  {activeTab === 'fisch' && (
                    <FischTab 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                      isSupplierMode={false}
                    />
                  )}
                  
                  {activeTab === 'einheiten' && (
                    <ArticleDetailEinheiten 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                      isSupplierMode={false}
                    />
                  )}
                  
                  {activeTab === 'vertrieb' && (
                    <ArticleDetailVertrieb 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                      isSupplierMode={false}
                    />
                  )}
                  
                  {activeTab === 'marketing' && (
                    <ArticleDetailMarketing 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                      isSupplierMode={false}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
                </>
              )}
            </ChangeTrackingWrapper>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-600 animate-spin mb-4" />
                <p className="text-gray-600">Lade Artikel...</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ArticleDetailModal;
